import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  Box,
  IconButton,
  Typography,
  Avatar,
  styled,
  Tooltip,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import { ChatUser } from "../types/chat";

interface ModalVideoCallProps {
  open: boolean;
  localStream: MediaStream | null;
  remoteStreams: Map<number, MediaStream>;
  participants: ChatUser[];
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onEndCall: () => void;
  onChangeAudioDevice?: (deviceId: string) => void;
  onChangeVideoDevice?: (deviceId: string) => void;
}

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    margin: 0,
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100vw",
    height: "100vh",
    borderRadius: 0,
    backgroundColor: "#1a1a1a",
    overflow: "hidden",
  },
});

const VideoContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100vh", // Usa viewport height para garantir altura total
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#000",
  overflow: "hidden", // Evita scroll
});

const VideosGrid = styled(Box)({
  flex: 1,
  display: "grid",
  gap: "8px",
  padding: "16px",
  paddingBottom: "96px", // Espaço para a barra de controles
  gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
  gridAutoRows: "minmax(0, 1fr)", // Garante que as linhas ocupem todo o espaço disponível
  alignItems: "stretch", // Estica os itens para preencher a célula
  justifyContent: "center",
  minHeight: 0, // Permite que o flex funcione corretamente
  height: "100%", // Garante altura total
});

const VideoBox = styled(Paper)({
  position: "relative",
  width: "100%",
  height: "100%",
  minHeight: 0, // Remove altura mínima para permitir flexbox total
  backgroundColor: "#2a2a2a",
  borderRadius: "12px",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Video = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const LocalVideoBox = styled(Paper)({
  position: "absolute",
  bottom: "100px",
  right: "24px",
  width: "240px",
  height: "180px",
  backgroundColor: "#2a2a2a",
  borderRadius: "12px",
  overflow: "hidden",
  border: "2px solid rgba(255, 255, 255, 0.2)",
  zIndex: 10,
});

const UserInfoOverlay = styled(Box)({
  position: "absolute",
  bottom: "12px",
  left: "12px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  padding: "8px 12px",
  borderRadius: "8px",
  backdropFilter: "blur(10px)",
});

const ControlsBar = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  gap: "16px",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  backdropFilter: "blur(20px)",
});

const ControlButton = styled(IconButton)(({ theme }) => ({
  width: "56px",
  height: "56px",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  "&.muted": {
    backgroundColor: "rgba(244, 67, 54, 0.9)",
    "&:hover": {
      backgroundColor: "rgba(244, 67, 54, 1)",
    },
  },
}));

const EndCallButton = styled(IconButton)({
  width: "64px",
  height: "64px",
  backgroundColor: "#f44336",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#d32f2f",
  },
});

const NoVideoPlaceholder = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  color: "#fff",
});

const ModalVideoCall: React.FC<ModalVideoCallProps> = ({
  open,
  localStream,
  remoteStreams,
  participants,
  onToggleAudio,
  onToggleVideo,
  onEndCall,
  onChangeAudioDevice,
  onChangeVideoDevice,
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const remoteVideoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());

  // Log para debug - monitorar mudanças nos participantes e streams
  useEffect(() => {
    console.log("🎬 ModalVideoCall - Estado atualizado:");
    console.log("   Participantes:", participants?.length || 0, participants?.map(p => ({ id: p.id, nome: p.nome })));
    console.log("   Remote Streams:", remoteStreams?.size || 0, Array.from(remoteStreams?.keys() || []));
    console.log("   Local Stream:", !!localStream);
  }, [participants, remoteStreams, localStream]);

  // Estados para seleção de dispositivos
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [audioMenuAnchor, setAudioMenuAnchor] = useState<null | HTMLElement>(null);
  const [videoMenuAnchor, setVideoMenuAnchor] = useState<null | HTMLElement>(null);

  // Enumerar dispositivos disponíveis
  useEffect(() => {
    const loadDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        
        const audioInputs = devices.filter((d) => d.kind === "audioinput");
        const videoInputs = devices.filter((d) => d.kind === "videoinput");
        
        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);

        // Definir dispositivos ativos atuais
        if (localStream) {
          const audioTrack = localStream.getAudioTracks()[0];
          const videoTrack = localStream.getVideoTracks()[0];
          
          if (audioTrack) {
            const audioSettings = audioTrack.getSettings();
            setSelectedAudioDevice(audioSettings.deviceId || "");
          }
          
          if (videoTrack) {
            const videoSettings = videoTrack.getSettings();
            setSelectedVideoDevice(videoSettings.deviceId || "");
          }
        } else {
          // Se não há stream ativo, carregar preferências salvas
          const savedAudioDevice = localStorage.getItem("preferredAudioDevice");
          const savedVideoDevice = localStorage.getItem("preferredVideoDevice");
          
          if (savedAudioDevice) {
            setSelectedAudioDevice(savedAudioDevice);
          }
          
          if (savedVideoDevice) {
            setSelectedVideoDevice(savedVideoDevice);
          }
        }
      } catch (error) {
        console.error("Erro ao enumerar dispositivos:", error);
      }
    };

    if (open) {
      loadDevices();
    }
  }, [open, localStream]);

  // Callback ref para configurar stream local assim que o elemento for montado
  const localVideoCallback = useCallback((videoElement: HTMLVideoElement | null) => {
    if (videoElement && localStream) {
      console.log("🎥 Configurando stream local no elemento:", {
        hasStream: !!localStream,
        videoTracks: localStream.getVideoTracks().length,
        audioTracks: localStream.getAudioTracks().length,
      });
      videoElement.srcObject = localStream;
      videoElement.muted = true; // Sempre mutar o próprio áudio localmente
      videoElement.play().catch(err => console.error("Erro ao reproduzir vídeo local:", err));
      localVideoRef.current = videoElement;
      console.log("✅ Stream local configurado e reproduzindo");
    }
  }, [localStream]);

  // Callback factory para configurar streams remotos
  const createRemoteVideoCallback = useCallback((userId: number, stream: MediaStream) => {
    return (videoElement: HTMLVideoElement | null) => {
      if (videoElement && stream) {
        console.log(`🎥 Configurando stream remoto do usuário ${userId}:`, {
          hasStream: !!stream,
          videoTracks: stream.getVideoTracks().length,
          audioTracks: stream.getAudioTracks().length,
        });
        videoElement.srcObject = stream;
        videoElement.play().catch(err => console.error(`Erro ao reproduzir vídeo remoto ${userId}:`, err));
        remoteVideoRefs.current.set(userId, videoElement);
        console.log(`✅ Stream remoto do usuário ${userId} configurado e reproduzindo`);
      }
    };
  }, []);

  // Funções de menu de dispositivos
  const handleOpenAudioMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAudioMenuAnchor(event.currentTarget);
  };

  const handleCloseAudioMenu = () => {
    setAudioMenuAnchor(null);
  };

  const handleOpenVideoMenu = (event: React.MouseEvent<HTMLElement>) => {
    setVideoMenuAnchor(event.currentTarget);
  };

  const handleCloseVideoMenu = () => {
    setVideoMenuAnchor(null);
  };

  const handleSelectAudioDevice = async (deviceId: string) => {
    setSelectedAudioDevice(deviceId);
    handleCloseAudioMenu();
    
    // Salvar preferência no localStorage
    localStorage.setItem("preferredAudioDevice", deviceId);
    
    if (onChangeAudioDevice) {
      onChangeAudioDevice(deviceId);
    }
  };

  const handleSelectVideoDevice = async (deviceId: string) => {
    setSelectedVideoDevice(deviceId);
    handleCloseVideoMenu();
    
    // Salvar preferência no localStorage
    localStorage.setItem("preferredVideoDevice", deviceId);
    
    if (onChangeVideoDevice) {
      onChangeVideoDevice(deviceId);
    }
  };

  const handleToggleAudio = () => {
    setAudioMuted(!audioMuted);
    onToggleAudio();
  };

  const handleToggleVideo = () => {
    setVideoOff(!videoOff);
    onToggleVideo();
  };

  const getUserName = (userId: number) => {
    const user = participants.find((p) => p.id === userId);
    return user?.nomecomp || user?.nome || "Usuário";
  };

  const getUserAvatar = (userId: number) => {
    const user = participants.find((p) => p.id === userId);
    return user?.img_usuario;
  };

  // Calcular número de colunas baseado no número de participantes
  const participantCount = remoteStreams.size || 1;
  const getGridColumns = () => {
    if (participantCount === 1) return "1fr";
    if (participantCount === 2) return "repeat(2, 1fr)";
    return "repeat(auto-fit, minmax(400px, 1fr))";
  };

  // Total de participantes na chamada (incluindo o usuário local)
  const totalParticipants = participants.length;
  
  // Determinar o texto do botão de encerrar baseado no número de participantes
  const getEndCallTooltip = () => {
    if (totalParticipants >= 3) {
      return "Sair da chamada (outros continuarão)";
    }
    return "Encerrar chamada";
  };

  return (
    <StyledDialog open={open} maxWidth={false} fullScreen>
      <VideoContainer>
        {/* Grid de vídeos remotos */}
        <VideosGrid
          sx={{
            gridTemplateColumns: getGridColumns(),
          }}
        >
          {Array.from(remoteStreams.entries()).map(([userId, stream]) => (
            <VideoBox key={userId} elevation={3}>
              <Video
                ref={createRemoteVideoCallback(userId, stream)}
                autoPlay
                playsInline
              />
              <UserInfoOverlay>
                <Avatar
                  src={getUserAvatar(userId)}
                  alt={getUserName(userId)}
                  sx={{ width: 32, height: 32 }}
                >
                  {getUserName(userId).charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="body2" color="#fff" fontWeight={500}>
                  {getUserName(userId)}
                </Typography>
              </UserInfoOverlay>
            </VideoBox>
          ))}

          {/* Placeholder quando não há participantes remotos ainda */}
          {remoteStreams.size === 0 && (
            <VideoBox elevation={3}>
              <NoVideoPlaceholder>
                <VideocamOffIcon sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} />
                <Typography variant="h6" sx={{ opacity: 0.7 }}>
                  Aguardando participantes...
                </Typography>
              </NoVideoPlaceholder>
            </VideoBox>
          )}
        </VideosGrid>

        {/* Vídeo local (canto inferior direito) */}
        <LocalVideoBox elevation={8}>
          {localStream && !videoOff ? (
            <>
              <Video
                ref={localVideoCallback}
                autoPlay
                playsInline
                muted
              />
              <UserInfoOverlay>
                <Typography variant="caption" color="#fff" fontWeight={500}>
                  Você
                </Typography>
              </UserInfoOverlay>
            </>
          ) : (
            <NoVideoPlaceholder>
              <VideocamOffIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              <Typography variant="caption" sx={{ opacity: 0.7, mt: 1 }}>
                Câmera desligada
              </Typography>
            </NoVideoPlaceholder>
          )}
        </LocalVideoBox>

        {/* Barra de controles */}
        <ControlsBar>
          {/* Controle de microfone com dropdown */}
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Tooltip title={audioMuted ? "Ligar microfone" : "Mutar microfone"}>
              <ControlButton
                onClick={handleToggleAudio}
                className={audioMuted ? "muted" : ""}
              >
                {audioMuted ? <MicOffIcon /> : <MicIcon />}
              </ControlButton>
            </Tooltip>
            
            <Tooltip title="Selecionar microfone">
              <IconButton
                onClick={handleOpenAudioMenu}
                sx={{
                  width: "40px",
                  height: "56px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Controle de câmera com dropdown */}
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Tooltip title={videoOff ? "Ligar câmera" : "Desligar câmera"}>
              <ControlButton
                onClick={handleToggleVideo}
                className={videoOff ? "muted" : ""}
              >
                {videoOff ? <VideocamOffIcon /> : <VideocamIcon />}
              </ControlButton>
            </Tooltip>
            
            <Tooltip title="Selecionar câmera">
              <IconButton
                onClick={handleOpenVideoMenu}
                sx={{
                  width: "40px",
                  height: "56px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Tooltip title={getEndCallTooltip()}>
            <EndCallButton onClick={onEndCall} size="large">
              <CallEndIcon />
            </EndCallButton>
          </Tooltip>
        </ControlsBar>

        {/* Menu de seleção de microfone */}
        <Menu
          anchorEl={audioMenuAnchor}
          open={Boolean(audioMenuAnchor)}
          onClose={handleCloseAudioMenu}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          {audioDevices.map((device) => (
            <MenuItem
              key={device.deviceId}
              onClick={() => handleSelectAudioDevice(device.deviceId)}
              selected={device.deviceId === selectedAudioDevice}
            >
              <ListItemIcon>
                {device.deviceId === selectedAudioDevice && (
                  <CheckIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {device.label || `Microfone ${audioDevices.indexOf(device) + 1}`}
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>

        {/* Menu de seleção de câmera */}
        <Menu
          anchorEl={videoMenuAnchor}
          open={Boolean(videoMenuAnchor)}
          onClose={handleCloseVideoMenu}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          {videoDevices.map((device) => (
            <MenuItem
              key={device.deviceId}
              onClick={() => handleSelectVideoDevice(device.deviceId)}
              selected={device.deviceId === selectedVideoDevice}
            >
              <ListItemIcon>
                {device.deviceId === selectedVideoDevice && (
                  <CheckIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {device.label || `Câmera ${videoDevices.indexOf(device) + 1}`}
              </ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </VideoContainer>
    </StyledDialog>
  );
};

export default ModalVideoCall;
